import { Link, router, usePage } from "@inertiajs/react"
import { Building2, Check, ChevronDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { companySwitchPath } from "@/lib/company-routes"
import { frontCompanies } from "@/routes"

/** Opens the same section of another company of the account; the URL names the company. */
export function switchCompany(companyId: number) {
  router.visit(companySwitchPath(window.location.pathname, companyId))
}

/** The current company next to the logo, with a menu to switch. Shown once the account has several companies. */
export function CompanySwitcher() {
  const companies = usePage().props.company_context
  if (!companies || companies.all.filter((company) => !company.archived).length < 2) return null

  const { current, all } = companies

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Company: ${current.name}. Switch company`}
        className="inline-flex max-w-40 items-center gap-1.5 rounded-md px-2 py-2 text-sm font-semibold text-inverse-foreground outline-none hover:bg-inverse-hover focus-visible:ring-2 focus-visible:ring-inverse-foreground sm:max-w-56"
      >
        <Building2 className="size-4 shrink-0 text-inverse-muted-foreground" aria-hidden="true" />
        <span className="truncate">{current.name}</span>
        <ChevronDown className="size-4 shrink-0 text-inverse-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch company</DropdownMenuLabel>
        {all.map((company) => (
          <DropdownMenuItem
            key={company.id}
            disabled={company.id === current.id || company.archived}
            onSelect={() => switchCompany(company.id)}
          >
            <span className="flex-1 truncate">{company.name}</span>
            {company.archived && <Badge variant="outline">Archived</Badge>}
            {company.id === current.id && <Check className="size-4" aria-label="Current company" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href={frontCompanies.index().url}>Manage companies</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
